"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Truck, Wrench, Search as SearchIcon, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger, PopoverAnchor } from "@/components/ui/popover";
import { ProductsSearch, useWPQueryForSearch, buildProductSearchUrl } from "@/components/shop/ProductsSearch";
import { EcommerceProductsProvider, useEcommerceProducts, ProductPrice } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
function SearchProductsConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="space-y-2 p-3">
        <div className="h-10 animate-pulse rounded-xs bg-muted" />
        <div className="h-10 animate-pulse rounded-xs bg-muted" />
      </div>;
  }
  if (error) {
    return <p className="p-3 text-xs text-destructive">Unable to load catalog results.</p>;
  }
  if (!products.length) {
    return <p className="p-3 text-xs text-muted-foreground">No matching tools or equipment found.</p>;
  }
  const visibleProducts = products.slice(0, 6);
  return <div className="grid grid-cols-1 gap-2 p-2 sm:grid-cols-2">
      {visibleProducts.map((product, i) => <Link key={product.id} to={product.link || `/ShopPage`} data-index={i} className="group flex items-center gap-3 rounded-xs border border-border bg-card p-2.5 text-left transition-[border-color,background-color] hover:border-primary hover:bg-accent">
          {product.featured_image_url ? <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xs bg-muted">
              <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover" loading="lazy" />
            </div> : <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xs bg-muted text-muted-foreground">
              <Wrench className="size-5" />
            </div>}
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold uppercase tracking-tight text-card-foreground group-hover:text-primary">
              {product.title}
            </p>
            <ProductPrice product={product} as="div" className="mt-0.5 text-xs font-bold text-primary" />
          </div>
        </Link>)}
    </div>;
}
const specBadges = [{
  icon: ShieldCheck,
  label: "Heavy-duty spec verified"
}, {
  icon: Truck,
  label: "Rapid workshop dispatch"
}, {
  icon: Wrench,
  label: "Industrial grade warranty"
}];
const quickCategoryLinks = [{
  label: "All tools",
  path: "/ShopPage"
}, {
  label: "Workshop gear",
  path: "/ShopPage"
}, {
  label: "Garage systems",
  path: "/ShopPage"
}, {
  label: "Maintenance",
  path: "/ShopPage"
}];
export default function Hero() {
  const [searchValue, setSearchValue] = React.useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const shouldReduceMotion = useReducedMotion();

  // Read search term from WordPress query parameters dynamically
  const [searchTerm, setSearchTerm] = React.useState("");
  React.useEffect(() => {
    try {
      const globalWindow = window as unknown as {
        wvcClient?: {
          getCurrentWPQueryParams?: () => Record<string, string | number | undefined>;
        };
      };
      if (typeof globalWindow.wvcClient !== "undefined" && typeof globalWindow.wvcClient.getCurrentWPQueryParams === "function") {
        const params = globalWindow.wvcClient.getCurrentWPQueryParams() || {};
        const term = String(params?.s ?? params?.search ?? "").trim();
        setSearchTerm(term);
      }
    } catch {
      setSearchTerm("");
    }
  }, []);
  const headlineText = searchTerm ? `Search results for "${searchTerm}"` : "Industrial depot & hardware catalog";
  const searchUrl = buildProductSearchUrl(searchValue);
  return <section id="shop-hero" data-nav="dark" className="relative overflow-hidden border-b border-border bg-background py-12 text-foreground sm:py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Monospace breadcrumb navigation */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.24,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-6 font-mono text-xs uppercase tracking-wider">
          <Breadcrumb>
            <BreadcrumbList className="text-muted-foreground">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/HomePage" className="text-muted-foreground hover:text-foreground">
                    Depot index
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-muted-foreground" />
              <BreadcrumbItem>
                <BreadcrumbPage className="font-semibold text-primary">
                  {searchTerm ? "Search query" : "Catalog archive"}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </motion.div>

        {/* Title Block with Precision Spec Rule (3px orange accent bar) */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.24,
        delay: 0.05,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative max-w-4xl">
          <div className="flex items-stretch gap-4 sm:gap-6">
            {/* Precision Spec Rule - 3px Safety Orange Bar */}
            <div className="w-[3px] shrink-0 self-stretch rounded-xs bg-primary" aria-hidden="true" />

            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-widest text-primary">
                <span>Huga Scalet LLC</span>
                <span className="text-muted-foreground">/</span>
                <span className="text-muted-foreground">Depot 33-SKU inventory</span>
              </div>

              <h1 className="font-serif text-3xl font-extrabold tracking-tight text-foreground uppercase sm:text-4xl lg:text-5xl">
                {headlineText}
              </h1>

              <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground sm:text-base">
                Heavy-gauge workshop tools, precision pneumatics, and fabrication hardware.
                Engineered for maintenance shops, commercial garages, and industrial trade facilities nationwide.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Catalog statistics and spec indicator badges */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.24,
        delay: 0.1,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-8 flex flex-wrap items-center gap-2 sm:gap-3">
          {specBadges.map((badge, i) => {
          const Icon = badge.icon;
          return <Badge key={badge.label} data-index={i} variant="secondary" className="gap-2 rounded-xs border border-border bg-card px-3 py-1.5 font-mono text-xs font-medium text-foreground transition-colors hover:border-primary">
                <Icon className="size-3.5 text-primary" />
                <span className="capitalize">{badge.label}</span>
              </Badge>;
        })}
        </motion.div>

        {/* Integrated Product Search & Category Filters */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.24,
        delay: 0.15,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <EcommerceProductsProvider wp_query={wp_query}>
              <Popover open={isSearchOpen} onOpenChange={setIsSearchOpen}>
                <div className="relative flex w-full items-center">
                  <div className="relative w-full">
                    <ProductsSearch value={searchValue} onChange={val => {
                    setSearchValue(val);
                    if (!isSearchOpen && val.trim() !== "") {
                      setIsSearchOpen(true);
                    }
                  }} placeholder="Search tools, part numbers, or gear..." className="w-full [&_input]:h-11 [&_input]:rounded-xs [&_input]:border-border [&_input]:bg-card [&_input]:pl-10 [&_input]:pr-4 [&_input]:font-sans [&_input]:text-sm [&_input]:text-foreground [&_input]:placeholder:text-muted-foreground [&_input]:focus-visible:border-primary [&_input]:focus-visible:ring-1 [&_input]:focus-visible:ring-primary" />
                    <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  </div>

                  <PopoverAnchor className="absolute inset-x-0 bottom-0" />
                </div>

                <PopoverContent align="start" sideOffset={6} className="w-(--radix-popover-trigger-width) min-w-[320px] max-w-2xl rounded-xs border border-border bg-popover p-0 shadow-2xl sm:min-w-[480px]">
                  <div className="border-b border-border bg-card px-3.5 py-2">
                    <p className="font-mono text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {hasSearchQuery ? `Live catalog matches for "${searchValue}"` : "Type to query catalog inventory"}
                    </p>
                  </div>

                  <div className="max-h-72 overflow-y-auto">
                    {!hasSearchQuery ? <p className="p-4 font-mono text-xs text-muted-foreground">
                        Enter a tool name, category, or specification above.
                      </p> : <>
                        <SearchProductsConsumer />
                        {searchUrl && <div className="border-t border-border bg-card p-2.5">
                            <Link to={searchUrl} className="inline-flex w-full items-center justify-between rounded-xs bg-secondary px-3 py-2 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-primary hover:text-primary-foreground">
                              <span>View all results for &ldquo;{searchValue}&rdquo;</span>
                              <ArrowRight className="size-3.5" />
                            </Link>
                          </div>}
                      </>}
                  </div>
                </PopoverContent>
              </Popover>
            </EcommerceProductsProvider>
          </div>

          {/* Rapid category jump chips */}
          <div className="flex flex-wrap items-center gap-1.5 lg:col-span-5 lg:justify-end">
            <span className="mr-1 hidden font-mono text-xs uppercase tracking-wider text-muted-foreground sm:inline-block">
              Quick filters:
            </span>
            {quickCategoryLinks.map((item, i) => <Button key={item.label} data-index={i} asChild variant="outline" size="sm" className="h-8 rounded-xs border-border bg-card font-mono text-xs text-foreground uppercase hover:border-primary hover:bg-accent hover:text-primary">
                <Link to={item.path}>{item.label}</Link>
              </Button>)}
          </div>
        </motion.div>
      </div>
    </section>;
}